import { useEffect } from "react";
import { useDraft } from "../vault/draft";
import { draftTitle, PARK_TTL_MS } from "../vault/draft-core";
import { useT } from "../i18n";
import { Icons } from "./icons";

// Offers back an item edit whose dialog was torn down with unsaved content — an auto-lock on tab
// hide/idle, a 401 bouncing the app to sign-in, or the vault selection changing under the dialog.
// The draft itself lives in vault/draft.ts (module memory, never on disk), which is what lets it
// outlive the AppShell unmount those events cause. Sits with the offline banners above the header.
export function DraftRestoreBanner({ onRestore }: { onRestore: () => void }) {
  const t = useT();
  const parked = useDraft((s) => s.parked);
  // Drop the offer (and scrub its plaintext) once it goes stale, rather than keeping a secret
  // reachable in memory for the rest of the session.
  useEffect(() => {
    if (!parked?.parkedAt) return;
    const left = parked.parkedAt + PARK_TTL_MS - Date.now();
    if (left <= 0) {
      useDraft.getState().wipe();
      return;
    }
    const id = window.setTimeout(() => useDraft.getState().wipe(), left);
    return () => window.clearTimeout(id);
  }, [parked]);
  if (!parked) return null;
  const title = draftTitle(parked);
  return (
    <div className="flex items-center gap-2 border-b border-accent/40 bg-accent/10 px-3 py-1 text-xs text-fg">
      <Icons.refresh className="h-3.5 w-3.5 shrink-0 text-accent" />
      <span>
        {t("An unsaved item was not stored")}
        {title ? <strong className="ml-1">{title}</strong> : null}
      </span>
      <button onClick={onRestore} className="ml-auto rounded border border-accent px-2 py-0.5 text-accent hover:bg-accent hover:text-white">
        {t("Restore")}
      </button>
      <button onClick={() => useDraft.getState().wipe()} className="rounded border border-border px-2 py-0.5 text-muted hover:text-fg">
        {t("Discard")}
      </button>
    </div>
  );
}
